import React, { useState, useEffect } from 'react';
import './ExtraServices.css';
import { Plus, Trash2, Edit, Save, X, Coffee, Utensils, Wifi, Car, Trash } from 'lucide-react';
import Sidebar from '../../components/layout/Sidebar/Sidebar';
import apiClient from '../../services/api';

const ExtraServices = () => {
    const [services, setServices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSvc, setEditingSvc] = useState(null);
    const [form, setForm] = useState({
        name: '',
        description: '',
        price: '',
        price_type: 'per_stay',
        icon: 'Coffee',
        status: 'Active'
    });

    const entity = JSON.parse(localStorage.getItem('entity') || '{}');
    const hotelId = entity.id;

    useEffect(() => {
        fetchServices();
    }, []);

    const fetchServices = async () => {
        try {
            setIsLoading(true);
            const res = await apiClient.get(`/extra-services/management/${hotelId}`);
            if (res.data && res.data.success) {
                setServices(res.data.data);
            }
        } catch (err) {
            console.error("Failed to fetch services:", err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = (svc = null) => {
        if (svc) {
            setEditingSvc(svc);
            setForm({ ...svc });
        } else {
            setEditingSvc(null);
            setForm({
                name: '',
                description: '',
                price: '',
                price_type: 'per_stay',
                icon: 'Coffee',
                status: 'Active'
            });
        }
        setIsModalOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            if (editingSvc) {
                await apiClient.put(`/extra-services/${editingSvc.id}`, form);
            } else {
                await apiClient.post('/extra-services', { ...form, hotel_id: hotelId });
            }
            setIsModalOpen(false);
            fetchServices();
        } catch (err) {
            alert('Failed to save service: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this service?')) return;
        try {
            await apiClient.delete(`/extra-services/${id}`);
            fetchServices();
        } catch (err) {
            alert('Delete failed');
        }
    };

    const icons = {
        'Coffee': <Coffee size={20} />,
        'Utensils': <Utensils size={20} />,
        'Wifi': <Wifi size={20} />,
        'Car': <Car size={20} />,
        'Star': <Save size={20} />
    };

    return (
        <div className="extra-services-page">
            <Sidebar />
            <main className="main-content">
                <div className="page-header">
                    <div>
                        <h1>Add-on Services</h1>
                        <p>Upsell your guests with extra services like airport pickup, breakfast, or laundry.</p>
                    </div>
                    <button className="add-btn" onClick={() => handleOpenModal()}>
                        <Plus size={18} /> Add New Service
                    </button>
                </div>

                {isLoading ? (
                    <div className="loading">Loading...</div>
                ) : (
                    <div className="services-grid">
                        {services.length > 0 ? services.map(svc => (
                            <div key={svc.id} className={`service-card ${svc.status === 'Inactive' ? 'inactive' : ''}`}>
                                <div className="card-top">
                                    <div className="icon-box">
                                        {icons[svc.icon] || <Coffee size={20} />}
                                    </div>
                                    <div className="svc-info">
                                        <h3>{svc.name}</h3>
                                        <p>{svc.description || 'No description provided'}</p>
                                    </div>
                                </div>
                                <div className="card-details">
                                    <div className="price-tag">
                                        <span className="amount">${svc.price}</span>
                                        <span className="type">/{svc.price_type.replace('_', ' ')}</span>
                                    </div>
                                    <div className="actions">
                                        <button className="action-btn edit" onClick={() => handleOpenModal(svc)}>
                                            <Edit size={16} />
                                        </button>
                                        <button className="action-btn delete" onClick={() => handleDelete(svc.id)}>
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )) : (
                            <div className="empty-state">
                                <p>You haven't added any extra services yet.</p>
                            </div>
                        )}
                    </div>
                )}

                {isModalOpen && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h2>{editingSvc ? 'Edit Service' : 'Add New Service'}</h2>
                                <button className="close-btn" onClick={() => setIsModalOpen(false)}><X size={20} /></button>
                            </div>
                            <form onSubmit={handleSave} className="svc-form">
                                <div className="form-group">
                                    <label>Service Name</label>
                                    <input 
                                        type="text" required placeholder="e.g. Airport Pickup"
                                        value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Description</label>
                                    <textarea 
                                        rows="3" placeholder="Tell guests about this service..."
                                        value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                                    />
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Price ($)</label>
                                        <input 
                                            type="number" step="0.01" required
                                            value={form.price} onChange={e => setForm({...form, price: e.target.value})}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Charge Frequency</label>
                                        <select value={form.price_type} onChange={e => setForm({...form, price_type: e.target.value})}>
                                            <option value="per_stay">Per Stay</option>
                                            <option value="per_night">Per Night</option>
                                            <option value="per_person">Per Person</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Icon</label>
                                        <select value={form.icon} onChange={e => setForm({...form, icon: e.target.value})}>
                                            <option value="Coffee">Catering / Cafe</option>
                                            <option value="Utensils">Restaurant / Food</option>
                                            <option value="Wifi">Internet / Wifi</option>
                                            <option value="Car">Transportation</option>
                                            <option value="Star">Featured / Star</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Status</label>
                                        <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
                                            <option value="Active">Active</option>
                                            <option value="Inactive">Inactive</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn-cancel" onClick={() => setIsModalOpen(false)}>Cancel</button>
                                    <button type="submit" className="btn-save">Save Service</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default ExtraServices;
